import {
  Button,
  Col,
  Form, Select,
  Row,
  Spin, Input, Collapse,
} from 'antd';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRequest } from 'ahooks';
import { every } from 'lodash';
import qs from 'query-string';
import { getApplicationDetail, confirmRelation } from '@/services/supplyChainFinance';
import AddList from '@/pages/supplyChainFinance/application/components/AddList';
import UserLogs from './UserLogs';
import './Detail.less';
import FileDownload from '@/components/FileDownload/FileDownload';

const formItemLayout = {
  labelCol: {
    sm: { span: 4 },
  },
  wrapperCol: {
    sm: { span: 20 },
  },
};

export default function Detail() {
  const [form] = Form.useForm();
  const Navigate = useNavigate();
  const { search } = useLocation();
  const [confirmType, setConfirmType] = useState(undefined);
  const {
    edit, id, applicantId, from,
  } = qs.parse(search);

  // 根据来源确定页面标题
  const pageTitle = from === 'declaration' ? '申报信息' : '企业关系确认';

  const { loading: detailLoading, data: detailData } = useRequest(() => getApplicationDetail(id, applicantId), {
    onSuccess(res) {
      if (res) {
        // 不设置 confirmType、refuseReason 等编辑字段，避免回显旧数据
        const {
          confirmType: resConfirmType,
          refuseReason,
          confirmMark: resConfirmMark,
          ...otherData
        } = res;
        // 编辑时保持审核信息为空，不回显
        if (edit) {
          form.setFieldsValue(otherData);
        } else {
          form.setFieldsValue(res);
        }
      }
    },
  });

  const { loading: confirmLoading, run: confirmRun } = useRequest(confirmRelation, {
    manual: true,
    onSuccess() {
      Navigate('/supplyChain/declareResult');
    },
  });

  const data = detailData || {};

  function onFinish(values) {
    const obj = {
      chainRelationId: data.chainRelationId,
      id: data.id,
      type: values.confirmType,
      mark: values.confirmMark,
      ...(values.confirmType === 2 && { refuseReason: values.refuseReason }),
    };
    confirmRun(obj);
  }

  const onCancel = () => {
    if (from === 'declaration') {
      Navigate('/supplyChain/declaration');
    } else {
      Navigate('/supplyChain/relationshipConfirm');
    }
  };

  const onSubmit = () => {
    form.validateFields().then((values) => {
      onFinish(values);
    });
  };

  // 检查企业经营信息的四个字段是否都有值
  const hasAllBusinessInfo = every([
    data.lastYearEmployeeNum,
    data.lastYearRevenue,
    data.lastYearTotalProfit,
    data.lastYearTotalAssets,
  ], (value) => value !== undefined && value !== null && value !== '');

  return (
    <div className="supply-chain-finance__detail">
      <div className="content-width">
        <div className="supply-chain-finance__info-title">
          <span>{pageTitle}</span>
        </div>
        <div className="margin-top20">
          <Spin spinning={detailLoading}>
            <Form
              {...formItemLayout}
              form={form}
              name="form"
              className="form"
              onFinish={onFinish}
            >
              <Row>
                <Col span={24} className="supply-chain-finance__info-sub-title">基本信息</Col>
                <Col span={12}>
                  企业名称/姓名：
                  {data.applicantName}
                </Col>
                <Col span={12}>
                  统一社会信用代码/身份证号码：
                  {data.applicantNo}
                </Col>
                {
                  data.registerTime
                  && (
                    <Col span={12}>
                      注册时间：
                      {data.registerTime}
                    </Col>
                  )
                }
                <Col span={12}>
                  所在区域：
                  {data.provinceCityArea}
                </Col>
                {data.legalMan && (
                  <Col span={12}>
                    企业法人：
                    {data.legalMan}
                  </Col>
                )}
                <Col span={12}>
                  联系人：
                  {data.contactName}
                </Col>
                <Col span={12}>
                  手机号：
                  {data.contactPhone}
                </Col>
                <Col span={12}>
                  联系地址：
                  {data.address}
                </Col>
                <Col span={24}>
                  主营业务：
                  {data.mianBusiness}
                </Col>
                <Col span={24}>
                  备注：
                  {data.remark || '-'}
                </Col>

                {hasAllBusinessInfo && (
                  <>
                    <Col span={24} className="supply-chain-finance__info-sub-title">企业经营信息</Col>
                    <Col span={12}>
                      上年度职工人数：
                      {data.lastYearEmployeeNum}
                    </Col>
                    <Col span={12}>
                      上年度营收总额：
                      {data.lastYearRevenue}
                      万元
                    </Col>
                    <Col span={12}>
                      上年度利润总额：
                      {data.lastYearTotalProfit}
                      万元
                    </Col>
                    <Col span={12}>
                      上年度资产总额：
                      {data.lastYearTotalAssets}
                      万元
                    </Col>
                  </>
                )}

                <Col span={24} className="supply-chain-finance__info-sub-title">产业链基本信息</Col>
                <Col span={12}>
                  所属产业链：
                  {data.industryChainBelongs}
                </Col>
                <Col span={12}>
                  项目名称：
                  {data.projectName}
                </Col>
                <Col span={12}>
                  是否有下游企业：
                  {data.isDown ? '是' : '否'}
                </Col>
                {data.isDown === 1 && (
                  <Col span={12}>
                    下游企业：
                    {data.downstreamEnterprises?.map((item) => item.applicantName).join('、')}
                  </Col>
                )}
                <Col span={12}>
                  是否有上游企业：
                  {data.isUp ? '是' : '否'}
                </Col>
                {data.isUp === 1 && (
                  <Col span={12}>
                    上游企业：
                    {data.uptreamEnterprises?.map((item) => item.applicantName).join('、')}
                  </Col>
                )}
                {
                  data.mainRoleValue && (
                    <Col span={12}>
                      主体角色：
                      {data.mainRoleValue}
                    </Col>
                  )
                }

                {/* 业务往来信息 */}
                {data.chainBusinessInfoVos?.map((businessInfo) => {
                  // 判断是否为上游企业
                  const isUpstreamEnterprise = data.uptreamEnterprises?.some(
                    (enterprise) => enterprise.applicantNo === businessInfo.applicantNo,
                  );

                  return (
                    <Col span={24} key={`business-${businessInfo.applicantNo}`}>
                      <Collapse defaultActiveKey={['1']}>
                        <Collapse.Panel header={`业务往来信息（与${businessInfo.applicantName}的业务往来信息）`} key="1">
                          <Row>
                            <Col span={24}>
                              <AddList
                                formRef={form}
                                title="订单"
                                name={`orderAmount_${businessInfo.applicantNo}`}
                                isEdit={false}
                                enterpriseName={businessInfo.applicantName}
                                isUpstream={isUpstreamEnterprise}
                                initialData={businessInfo.orders}
                              />
                            </Col>
                            <Col span={24}>
                              <AddList
                                formRef={form}
                                title="发票"
                                name={`invoiceAmount_${businessInfo.applicantNo}`}
                                isEdit={false}
                                enterpriseName={businessInfo.applicantName}
                                isUpstream={isUpstreamEnterprise}
                                initialData={businessInfo.invoices}
                              />
                            </Col>
                          </Row>
                        </Collapse.Panel>
                      </Collapse>
                    </Col>
                  );
                })}

                {
                  data.type !== 1
                  && (
                    <>
                      <Col span={24} className="supply-chain-finance__info-sub-title">附件材料</Col>
                      <Col span={24}>
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                            与关联企业合作协议
                            <span className="sub-label" style={{ fontWeight: 'normal' }}>(需明确合作期限、合作内容等)</span>
                            ：
                          </div>
                          <div>
                            {data.cooperationAgreementFiles?.length > 0 ? (
                              data.cooperationAgreementFiles.map((item, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <FileDownload key={index} appendixName={item.fileName} fileUrl={item.fileUrl} />
                              ))
                            ) : '-'}
                          </div>
                        </div>
                      </Col>
                      <Col span={24}>
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ fontWeight: 'bold', marginBottom: 8 }}>其他必要材料：</div>
                          <div>
                            {data.appendixFiles?.length > 0 ? (
                              data.appendixFiles.map((item, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <FileDownload key={index} appendixName={item.fileName} fileUrl={item.fileUrl} />
                              ))
                            ) : '-'}
                          </div>
                        </div>
                      </Col>
                    </>
                  )
                }

                {
                  data.userLogs && data.userLogs.length > 0 && !edit && (
                    <Col span={24}>
                      <UserLogs userLogs={data.userLogs} />
                    </Col>
                  )
                }

                {
                  edit && (
                    <>
                      <Col span={24} className="supply-chain-finance__info-sub-title">关系确认</Col>
                      <Col span={24}>
                        <Form.Item
                          label="确认结果"
                          name="confirmType"
                          rules={[
                            {
                              required: true,
                              message: '请选择确认结果',
                            },
                          ]}
                        >
                          <Select
                            placeholder="请选择确认结果"
                            onChange={(value) => setConfirmType(value)}
                            options={[
                              { label: '确认通过', value: 1 },
                              { label: '确认不通过', value: 2 },
                            ]}
                          />
                        </Form.Item>
                        {confirmType === 2 && (
                          <Form.Item
                            label="不通过原因"
                            name="refuseReason"
                            rules={[
                              {
                                required: true,
                                message: '请输入不通过原因',
                              },
                            ]}
                          >
                            <Input.TextArea maxLength={500} showCount placeholder="请输入不通过原因" />
                          </Form.Item>
                        )}
                        <Form.Item name="confirmMark" label="备注">
                          <Input.TextArea maxLength={500} showCount placeholder="请输入备注" />
                        </Form.Item>
                      </Col>
                    </>
                  )
                }
              </Row>
              <div className="submit-btn-box" hidden={!edit}>
                <Button type="primary" ghost onClick={onCancel} style={{ marginRight: 20 }}>
                  取消
                </Button>
                <Button type="primary" loading={confirmLoading} onClick={onSubmit}>
                  提交
                </Button>
              </div>
            </Form>
          </Spin>
        </div>
      </div>

    </div>
  );
}
